import { z } from 'zod';
import { recipeSchema, recipeSummarySchema } from './recipes.ts';

/** Friend codes: 8 characters without look-alikes (0/O, 1/I/L). */
export const FRIEND_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const friendCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .transform((code) => code.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(/^[A-HJ-KM-NP-Z2-9]{8}$/));

export const friendSchema = z.object({
  householdId: z.uuid(),
  name: z.string(),
  since: z.iso.datetime(),
});
export type Friend = z.infer<typeof friendSchema>;

/** `GET /api/friends`: my code (made on first use) and my friends. */
export const friendsSchema = z.object({
  code: z.string(),
  friends: z.array(friendSchema),
});
export type Friends = z.infer<typeof friendsSchema>;

export const addFriendSchema = z.object({ code: friendCodeSchema });

/** `POST /api/friends/suggestions`: send one of my recipes to friends. */
export const sendSuggestionSchema = z.object({
  recipeId: z.uuid(),
  to: z.array(z.uuid()).min(1).max(20),
  message: z.string().trim().max(500).nullable().default(null),
});
export type SendSuggestion = z.input<typeof sendSuggestionSchema>;

/** A suggestion in my inbox. */
export const suggestionSchema = z.object({
  id: z.uuid(),
  fromHousehold: z.string(),
  fromUser: z.string().nullable(),
  message: z.string().nullable(),
  status: z.enum(['new', 'saved', 'dismissed']),
  savedRecipeId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  /** The sender's recipe as a card (their figures, never their notes or rating). */
  recipe: recipeSummarySchema,
});
export type Suggestion = z.infer<typeof suggestionSchema>;
export const inboxSchema = z.object({ suggestions: z.array(suggestionSchema) });

/** One suggestion with the whole recipe, for the preview. */
export const suggestionDetailSchema = suggestionSchema.extend({ full: recipeSchema });
export type SuggestionDetail = z.infer<typeof suggestionDetailSchema>;
